// src/pages/Jobs/JobListingLayout.tsx
import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, CardBody, Button, ButtonGroup } from 'reactstrap';
import { Map, List, Maximize2, Minimize2 } from 'lucide-react';
import JobCardGrid from './JobCardGrid';
import JobMap from './JobMap';
import { Job } from '../../models/Job.interface';

interface JobListingLayoutProps {
  jobs: Job[];
  loading: boolean;
  center?: {
    latitude: number;
    longitude: number;
  };
  showDistance?: boolean;
  userLocation?: {
    latitude: number;
    longitude: number;
  };
  onJobClick?: (job: Job) => void;
  onBoundsChange?: (bounds: {
    north: number;
    south: number;
    east: number;
    west: number;
  }) => void;
  onRequeryListings?: (center: { latitude: number; longitude: number }, bounds?: { north: number; south: number; east: number; west: number }) => void;
  isRequerying?: boolean;
  title?: string;
  subtitle?: string;
  totalCount?: number;
  showMap?: boolean;
  showFilters?: boolean;
  filterOptions?: {
    showLocationFilter?: boolean;
    showCategoryFilter?: boolean;
    showStatusFilter?: boolean;
  };
}

type ViewMode = 'list' | 'map' | 'split';

const JobListingLayout: React.FC<JobListingLayoutProps> = ({
  jobs,
  loading,
  center,
  showDistance = false,
  userLocation,
  onJobClick,
  onBoundsChange,
  onRequeryListings,
  isRequerying = false,
  title = 'Jobs',
  subtitle,
  totalCount,
  showMap = true,
  showFilters = false,
  filterOptions = {}
}) => {
  const [viewMode, setViewMode] = useState<ViewMode>('split');
  const [highlightedJobId, setHighlightedJobId] = useState<string | undefined>();
  const [splitPosition, setSplitPosition] = useState(67); // Changed from 50 to 67
  const [isDragging, setIsDragging] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  // Detect mobile view
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
      if (window.innerWidth < 768) {
        setViewMode('list');
      } else if (showMap) {
        setViewMode('split');
      } else {
        setViewMode('list');
      }
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, [showMap]);

  const handleJobHover = (job: Job | null) => {
    setHighlightedJobId(job?.id);
  };

  const handleJobClick = (job: Job) => {
    setHighlightedJobId(job.id);
    onJobClick?.(job);
  };

  // Handle mouse events for resizer
  const handleMouseDown = (e: React.MouseEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };

  useEffect(() => {
    if (!isDragging) return;

    const handleMouseMove = (e: MouseEvent) => {
      const container = document.getElementById('split-container');
      if (!container) return;

      const rect = container.getBoundingClientRect();
      const newPosition = ((e.clientX - rect.left) / rect.width) * 100;
      
      // Constrain between 20% and 80%
      const constrainedPosition = Math.max(20, Math.min(80, newPosition));
      setSplitPosition(constrainedPosition);
    };

    const handleMouseUp = () => {
      setIsDragging(false);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging]);

  return (
    <Container fluid className="py-0">
      {/* Header */}
      <Row className="mb-4">
        <Col>
          <div className="d-flex justify-content-between align-items-center flex-wrap gap-3">
            <div>
              <h1 className="h3 mb-1">{title}</h1>
              {subtitle && <p className="text-muted mb-0">{subtitle}</p>}
              {totalCount !== undefined && (
                <small className="text-muted">
                  {totalCount.toLocaleString()} job{totalCount !== 1 ? 's' : ''} found
                </small>
              )}
            </div>
            
            {/* View Toggle - Desktop Only */}
            {!isMobile && showMap && (
              <ButtonGroup>
                <Button
                  size="sm"
                  color={viewMode === 'list' ? 'primary' : 'outline-primary'}
                  onClick={() => setViewMode('list')}
                >
                  <List size={16} className="me-1" />
                  List Only
                </Button>
                <Button
                  size="sm"
                  color={viewMode === 'split' ? 'primary' : 'outline-primary'}
                  onClick={() => setViewMode('split')}
                >
                  <Maximize2 size={16} className="me-1" />
                  Split View
                </Button>
                <Button
                  size="sm"
                  color={viewMode === 'map' ? 'primary' : 'outline-primary'}
                  onClick={() => setViewMode('map')}
                >
                  <Map size={16} className="me-1" />
                  Map Only
                </Button>
              </ButtonGroup>
            )}
          </div>
        </Col>
      </Row>

      {/* Desktop Content */}
      {viewMode === 'list' || !showMap ? (
        // List only view - FIXED: Proper container height and scrolling
        <div style={{
          height: 'calc(100vh - 300px)',
          overflowY: 'auto',
          border: '1px solid #dee2e6',
          borderRadius: '0.375rem',
          padding: '1rem',
          backgroundColor: '#fff'
        }}>
          <JobCardGrid
            jobs={jobs}
            loading={loading}
            highlightedJobId={highlightedJobId}
            onJobHover={handleJobHover}
            onJobClick={handleJobClick}
            showDistance={showDistance}
            userLocation={userLocation}
          />
        </div>
      ) : viewMode === 'map' ? (
        // Map only view
        <Card style={{ height: 'calc(100vh - 200px)' }}>
          <CardBody className="p-0">
            <JobMap
              jobs={jobs}
              center={center}
              highlightedJobId={highlightedJobId}
              onJobClick={handleJobClick}
              onJobHover={handleJobHover}
              onBoundsChange={onBoundsChange}
              onRequeryListings={onRequeryListings}
              isRequerying={isRequerying}
              style={{ height: '100%', borderRadius: '0.375rem' }}
            />
          </CardBody>
        </Card>
      ) : (
        // Split view - FIXED: Proper heights and scrolling
        <div
          id="split-container"
          className="position-relative"
          style={{
            height: 'calc(100vh - 200px)',
            display: 'flex',
            cursor: isDragging ? 'col-resize' : 'default'
          }}
        >
          {/* Left panel - Job List */}
          <div
            style={{
              width: `${splitPosition}%`,
              overflow: 'hidden',
              borderRight: '1px solid #dee2e6'
            }}
          >
            <Card className="h-100">
              <CardBody className="p-2 h-100">
                {/* FIXED: Proper scrolling container */}
                <div style={{
                  height: '100%',
                  overflowY: 'auto',
                  overflowX: 'hidden'
                }}>
                  <JobCardGrid
                    jobs={jobs}
                    loading={loading}
                    highlightedJobId={highlightedJobId}
                    onJobHover={handleJobHover}
                    onJobClick={handleJobClick}
                    showDistance={showDistance}
                    userLocation={userLocation}
                  />
                </div>
              </CardBody>
            </Card>
          </div>

          {/* Resizer */}
          <div
            className="position-absolute bg-primary"
            style={{
              left: `${splitPosition}%`,
              top: 0,
              bottom: 0,
              width: '4px',
              cursor: 'col-resize',
              transform: 'translateX(-2px)',
              zIndex: 10,
              opacity: 0.3,
              transition: isDragging ? 'none' : 'opacity 0.2s'
            }}
            onMouseDown={handleMouseDown}
            onMouseEnter={(e) => e.currentTarget.style.opacity = '0.6'}
            onMouseLeave={(e) => e.currentTarget.style.opacity = '0.3'}
          />

          {/* Right panel - Map */}
          <div
            style={{
              width: `${100 - splitPosition}%`,
              overflow: 'hidden'
            }}
          >
            <Card className="h-100">
              <CardBody className="p-0">
                <JobMap
                  jobs={jobs}
                  center={center}
                  highlightedJobId={highlightedJobId}
                  onJobClick={handleJobClick}
                  onJobHover={handleJobHover}
                  onBoundsChange={onBoundsChange}
                  onRequeryListings={onRequeryListings}
                  isRequerying={isRequerying}
                  style={{ height: '100%', borderRadius: '0.375rem' }}
                />
              </CardBody>
            </Card>
          </div>
        </div>
      )}
    </Container>
  );
};

export default JobListingLayout;